import clsx from "clsx";

import {
  categoricalSlotByHash,
  categoricalColorVars,
} from "./categorical-color";

import styles from "./avatar.module.css";

export type AvatarSize = "sm" | "md" | "lg";

export interface AvatarProps {
  /** The person's display name — initials are derived from it (first letter
   *  of the first two words, or the first two characters of a single
   *  word), matching the retired `initialsOf` in `user-groups.tsx` and
   *  `selectors.tsx`. */
  name: string;
  /** A stable per-person key (typically the user id) hashed into a B1
   *  categorical slot — matches the retired `avatarColorOf`'s
   *  char-code-sum hash exactly (B1's `categoricalSlotByHash`), so an
   *  already-assigned colour does not reshuffle. */
  colorKey: string;
  /** `"sm"` (20px, chip adornment) / `"md"` (30px, table row — default) /
   *  `"lg"` (46px, drawer header). See `avatar.module.css` for the three
   *  retired geometries this replaces. */
  size?: AvatarSize;
  "data-test-role"?: string;
}

function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

/**
 * A coloured-initials identity circle (SR-301 phase 8 doc §4b) — retires
 * `initialsOf` + `avatarColorOf` + the hand-rolled circle markup duplicated
 * across `user-groups.tsx` (`.avatar`, `.chipAvatar`) and `selectors.tsx`
 * (`.avatar`, `.drawerAvatar`). Consumes B1's hash strategy for colour.
 */
function Avatar({
  name,
  colorKey,
  size = "md",
  "data-test-role": testRole,
}: AvatarProps) {
  const slot = categoricalSlotByHash(colorKey);
  const { color } = categoricalColorVars(slot);
  return (
    <span
      className={clsx(styles.avatar, styles[size])}
      style={{ backgroundColor: color }}
      data-test-role={testRole}
    >
      {initialsOf(name)}
    </span>
  );
}

export default Avatar;
